/* eslint-disable no-console */
import { EMPLEADOS_NETEO_MOTOR_DIRECTO } from "../constants/previsional";
import { client } from "./supabase";

export const TIPOS_PAGO_EMPLEADO = ["sueldo", "adelanto", "comision", "bono", "finiquito", "transporte"];
export const TIPOS_REQUIEREN_PERIODO = ["sueldo", "adelanto", "comision"];

// Tipos de pago que COMPONEN líquido (netean contra el devengado del mes). `transporte` queda afuera
// (asignación de movilización, se paga aparte); `comision`/`bono`/`finiquito` tampoco netean acá.
// FUENTE ÚNICA de esta lista (dominio Empleados/RRHH): la consumen `sumarPagadoLiquido` y
// `saldoLiquidoPorPeriodo` (regla 10.1).
export const TIPOS_COMPONEN_LIQUIDO = ["sueldo", "adelanto"];

// Tolerancia de redondeo del saldo, central. Un |saldo| por debajo de este umbral es ruido del
// recálculo del motor (AFP/salud/impuesto redondean al peso) o una miga, no una deuda real: no se
// emite fila. ~$1.000 (CLP/PEN son monedas sin decimales de uso corriente).
export const TOLERANCIA_SALDO_LIQUIDO = 1000;

export const getPagosEmpleados = async ({ pais, desde, hasta, empleadoId } = {}) => {
  try {
    let q = client
      .from("pagos_empleados")
      .select("*, empleados!inner(id, nombre, apellido, pais)")
      .order("fecha_pago", { ascending: false });
    if (pais) q = q.eq("empleados.pais", pais);
    if (desde) q = q.gte("fecha_pago", desde);
    if (hasta) q = q.lte("fecha_pago", hasta);
    if (empleadoId) q = q.eq("empleado_id", empleadoId);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en getPagosEmpleados:", error);
    throw error;
  }
};

export const getPagosPorMovimientos = async (movIds = []) => {
  if (movIds.length === 0) return [];
  try {
    // Embed empleados(nombre, apellido) vía FK pagos_empleados_empleado_id_fkey — alimenta el tooltip
    // del badge "E" (desglose por concepto) en FlujoCajaV2. Join, no query extra; sirve Chile y Perú.
    const { data, error } = await client
      .from("pagos_empleados")
      .select("*, empleados(nombre, apellido)")
      .in("movimiento_bancario_id", movIds);
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en getPagosPorMovimientos:", error);
    throw error;
  }
};

export const getPagosEmpleadoMes = async (empleadoId, año, mes) => {
  try {
    const { data, error } = await client
      .from("pagos_empleados")
      .select("*")
      .eq("empleado_id", empleadoId)
      .eq("periodo_año", año)
      .eq("periodo_mes", mes);
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en getPagosEmpleadoMes:", error);
    throw error;
  }
};

// ─── Haberes imponibles por-mes (haberes_empleado) ──────────────────────────
// Bono one-off / comisión del mes / otros haberes atados a un período puntual (a diferencia de
// bonos_fijo/variable de la vigencia, que son fijos). Se pasan como `haberesMes` a
// calcularLiquidacionChile. Ver migración 20260730_haberes_empleado + FASE 3.1a/3.1b.

// Un empleado/mes → array {monto, imponible, tipo, concepto}. Puede haber varias filas (ej. la
// comisión de Nicolás llega desglosada por mes de facturación).
export const getHaberesEmpleadoMes = async (empleadoId, año, mes) => {
  try {
    const { data, error } = await client
      .from("haberes_empleado")
      .select("monto, imponible, tipo, concepto")
      .eq("empleado_id", empleadoId)
      .eq("periodo_año", año)
      .eq("periodo_mes", mes);
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en getHaberesEmpleadoMes:", error);
    throw error;
  }
};

// Todos los haberes de un empleado (todos los períodos), con período incluido. Para consumidores que
// arman pendientes/proyección multi-mes (ej. getPendientesRemuneracion) sin N+1 por mes.
export const getHaberesEmpleado = async (empleadoId) => {
  try {
    const { data, error } = await client
      .from("haberes_empleado")
      .select("periodo_año, periodo_mes, monto, imponible, tipo, concepto")
      .eq("empleado_id", empleadoId);
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en getHaberesEmpleado:", error);
    throw error;
  }
};

// Batch por país (una sola query, sin N+1 por empleado-mes) — para FlujoCaja, que proyecta muchos
// meses. Trae empleado_id + período para indexar del lado del consumidor.
export const getHaberesPais = async (pais) => {
  try {
    let q = client
      .from("haberes_empleado")
      .select("empleado_id, periodo_año, periodo_mes, monto, imponible, tipo, concepto, empleados!inner(pais)");
    if (pais) q = q.eq("empleados.pais", pais);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en getHaberesPais:", error);
    throw error;
  }
};

// ─── Override de fecha de pago del sueldo líquido (pago_sueldo_fecha) ────────
// Batch por país (patrón getHaberesPais, sin N+1). Devuelve las filas
// {empleado_id, periodo_año, periodo_mes, fecha_pago}; el consumidor (FlujoCajaV2.rrhhEntries) las
// indexa por empleado/período. Sin fila → la proyección usa el último día del mes. Ver FASE 8.1.
export const getFechasPagoPais = async (pais) => {
  try {
    let q = client
      .from("pago_sueldo_fecha")
      .select("empleado_id, periodo_año, periodo_mes, fecha_pago, empleados!inner(pais)");
    if (pais) q = q.eq("empleados.pais", pais);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en getFechasPagoPais:", error);
    throw error;
  }
};

// Setea/actualiza el override de fecha de pago del sueldo (upsert sobre la UNIQUE
// empleado_id+periodo_año+periodo_mes). Sin override → la proyección usa el último día del mes.
export const setFechaPagoSueldo = async (empleadoId, año, mes, fecha) => {
  try {
    const { data, error } = await client
      .from("pago_sueldo_fecha")
      .upsert(
        { empleado_id: empleadoId, periodo_año: Number(año), periodo_mes: Number(mes), fecha_pago: fecha },
        { onConflict: "empleado_id,periodo_año,periodo_mes" }
      )
      .select();
    if (error) throw error;
    return data?.[0];
  } catch (error) {
    console.error("Error en setFechaPagoSueldo:", error);
    throw error;
  }
};

// Borra el override → la fila de sueldo vuelve al default (último día del mes).
export const eliminarFechaPagoSueldo = async (empleadoId, año, mes) => {
  try {
    const { error } = await client
      .from("pago_sueldo_fecha")
      .delete()
      .eq("empleado_id", empleadoId)
      .eq("periodo_año", Number(año))
      .eq("periodo_mes", Number(mes));
    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error en eliminarFechaPagoSueldo:", error);
    throw error;
  }
};

// Alta de un haber (bono/comisión/otro). origen='manual' lo setea el caller (el motor usa
// 'motor_comision' en 3.2). Múltiples filas por empleado/mes permitidas.
export const crearHaberEmpleado = async (row) => {
  try {
    const { data, error } = await client.from("haberes_empleado").insert(row).select();
    if (error) throw error;
    return data?.[0];
  } catch (error) {
    console.error("Error en crearHaberEmpleado:", error);
    throw error;
  }
};

// Baja de un haber. Solo se debe invocar sobre filas origen='manual' (las 'motor_comision' las
// gestiona el motor); la UI no expone la acción en esas filas.
export const eliminarHaberEmpleado = async (id) => {
  try {
    const { error } = await client.from("haberes_empleado").delete().eq("id", id);
    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error en eliminarHaberEmpleado:", error);
    throw error;
  }
};

export const crearPagosEmpleado = async (rows = []) => {
  if (rows.length === 0) return [];
  try {
    const { data, error } = await client.from("pagos_empleados").insert(rows).select();
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error en crearPagosEmpleado:", error);
    throw error;
  }
};

export const actualizarPagoEmpleado = async (id, cambios) => {
  try {
    const { data, error } = await client.from("pagos_empleados").update(cambios).eq("id", id).select();
    if (error) throw error;
    return data?.[0];
  } catch (error) {
    console.error("Error en actualizarPagoEmpleado:", error);
    throw error;
  }
};

export const eliminarPagoEmpleado = async (id) => {
  try {
    const { error } = await client.from("pagos_empleados").delete().eq("id", id);
    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error en eliminarPagoEmpleado:", error);
    throw error;
  }
};

export const eliminarPagosPorMovimiento = async (movId) => {
  try {
    const { error } = await client.from("pagos_empleados").delete().eq("movimiento_bancario_id", movId);
    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error en eliminarPagosPorMovimiento:", error);
    throw error;
  }
};

/**
 * Retorna un Set de empleado_id (uuid) que ya tienen al menos un pago tipo='finiquito'.
 */
export async function getEmpleadoIdsConFiniquito(pais) {
  try {
    let q = client.from("pagos_empleados").select("empleado_id, empleados!inner(pais)").eq("tipo", "finiquito");
    if (pais) q = q.eq("empleados.pais", pais);
    const { data, error } = await q;
    if (error) throw error;
    return new Set((data || []).map((p) => p.empleado_id));
  } catch (error) {
    console.error("Error en getEmpleadoIdsConFiniquito:", error);
    throw error;
  }
}

// Suma de lo ya pagado para un empleado en un período dado, solo tipos que restan del líquido devengado
export const sumarPagadoLiquido = (pagos, empleadoId, año, mes) =>
  pagos
    .filter(
      (p) =>
        p.empleado_id === empleadoId &&
        p.periodo_año === año &&
        p.periodo_mes === mes &&
        TIPOS_COMPONEN_LIQUIDO.includes(p.tipo)
    )
    .reduce((s, p) => s + Number(p.monto || 0), 0);

// Comisión YA PAGADA a un empleado — FUENTE ÚNICA (10.1): Σ de sus filas `pagos_empleados` tipo
// 'comision'. Toda fila de pagos_empleados es un desembolso real (a diferencia del proxy viejo, que
// sumaba haberes_empleado con período pasado); por eso NO se filtra por período — un pago cuenta como
// pagado aunque su período de pago sea el mes en curso (ej. el pago de agosto de Nicolás). Alimenta el
// `pagadoTotal` de comisionPorGeneracion (pendiente = cobrado − pagado). Reemplaza el conteo por
// haberes; el backfill llevó a pagos_empleados las comisiones que solo existían como haber.
export const sumarPagadoComision = (pagos, empleadoId) =>
  (pagos || [])
    .filter((p) => p.empleado_id === empleadoId && p.tipo === "comision")
    .reduce((s, p) => s + Number(p.monto || 0), 0);

// ─── Cuenta corriente del empleado — imputación ESTRICTA por período ──────────────────────────────
//
// Fuente ÚNICA de verdad del saldo de sueldo por empleado/mes (regla 10.1). Reemplaza el modelo FIFO
// de `netearEmpleado.js` (bolsa única aplicada del mes más viejo al más nuevo, que ignoraba el
// `periodo_mes/año` declarado en cada pago y hacía que un adelanto tageado a un mes descontara de
// otro). Regla ÚNICA confirmada por Ignacio (2026-08-20), sin excepciones:
//
//     saldo_mes = devengado_mes − pagado_mes
//
//   • `pagado_mes` = Σ de TODO lo tageado a ESE mes que compone líquido (sueldo+adelanto), vía
//     `sumarPagadoLiquido`. Se descuenta SOLO del mes que el pago declara — nunca de otro.
//   • Sin `max(0)`: si `pagado_mes > devengado_mes` (sobrepago, o adelanto a un mes cuyo devengado
//     real todavía no se cargó) el saldo se muestra NEGATIVO tal cual, sin ocultarlo.
//   • Sin arrastre en NINGUNA dirección: un excedente NO se traslada solo a otro mes. Si algo debe
//     contar para otro período, se tagea explícitamente a ese período.
//
// El DEVENGADO por mes es idéntico al de `netearEmpleado` — ancla al recibo + allowlist intactas
// (siguen gateadas por P3.15/P3.16, fuera del alcance de esta migración):
//   • Mes CERRADO (∃ pago tipo='sueldo' del período) y empleado FUERA de la allowlist → el devengado
//     se ANCLA al recibo = Σ líquido transferido del período = `pagado_mes` → saldo 0. Tapa el
//     "saldo fantasma" cuando el motor sobre-estima un mes ya liquidado por deuda de datos.
//   • Mes ABIERTO, o empleado en `EMPLEADOS_NETEO_MOTOR_DIRECTO` → devengado = estimación del motor
//     (`devengadoMotor`, que el llamador computa con `calcularLiquidacion<País>`).
//
// `empleadoId` scopea los pagos (`sumarPagadoLiquido` filtra por `empleado_id`) y decide la allowlist
// (comparación por ID — regla 10.7). Forma lista para Perú: el llamador solo cambia cómo calcula
// `devengadoMotor`; la imputación es idéntica.
//
// @param {Array<{año:number, mes:number, devengadoMotor:number}>} devengados  Un item por período con
//        remuneración vigente. Cualquier orden — la función ordena viejo→nuevo.
// @param {Array<{tipo:string, monto:number|string, empleado_id, periodo_año:number, periodo_mes:number}>} pagos
//        Filas de `pagos_empleados` del empleado.
// @param {string|number|null} empleadoId  ID del empleado (scope + allowlist).
// @param {number} tolerancia  Umbral bajo el cual (en |saldo|) `emitir=false`.
// @returns {{subledger: Array<{año, mes, devengado, pagado, saldo, tieneRecibo, emitir}>, tolerancia:number}}
//        Solo emite filas para los meses de `devengados`; un pago tageado a un mes SIN devengado no
//        genera fila (la auditoría 2026-08-20 confirmó 0 casos así — se revisita si aparecen).
export const saldoLiquidoPorPeriodo = ({
  devengados = [],
  pagos = [],
  empleadoId = null,
  tolerancia = TOLERANCIA_SALDO_LIQUIDO,
} = {}) => {
  const neteoMotorDirecto = empleadoId != null && EMPLEADOS_NETEO_MOTOR_DIRECTO.includes(empleadoId);

  // Meses con recibo emitido (≥1 pago tipo='sueldo' del período) para este empleado.
  const reciboEmitido = new Set();
  for (const p of pagos || []) {
    if (p.tipo === "sueldo" && p.empleado_id === empleadoId) reciboEmitido.add(`${p.periodo_año}-${p.periodo_mes}`);
  }

  const orden = [...devengados].sort((a, b) => a.año - b.año || a.mes - b.mes);
  const subledger = orden.map(({ año, mes, devengadoMotor }) => {
    const pagado = sumarPagadoLiquido(pagos, empleadoId, año, mes);
    const tieneRecibo = reciboEmitido.has(`${año}-${mes}`);
    // Mes cerrado → anclar al recibo (Σ líquido = pagado) salvo allowlist. Mes abierto → motor.
    const devengado = tieneRecibo && !neteoMotorDirecto ? pagado : Number(devengadoMotor) || 0;
    const saldo = devengado - pagado; // sin max(0), sin arrastre
    return { año, mes, devengado, pagado, saldo, tieneRecibo, emitir: Math.abs(saldo) > tolerancia };
  });

  return { subledger, tolerancia };
};
